<script lang="ts">
const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_formatted_value.name',
	description: '$t:blocks.display_formatted_value.description',
	icon: 'lucide:a-large-small',
	category: 'display',
	hidden: true,
	options: [
		{ name: 'fontFamily', type: 'enum', values: ['sans', 'serif', 'mono', 'display'] },
		{ name: 'weight', type: 'enum', values: ['thin', 'extralight', 'light', 'normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'] },
	],

	fieldBinding: {
		supportedTypes: ['identifier', 'text', 'number'],
		defaultProps: ({ fieldTypeId }) => {
			if (fieldTypeId === 'identifier') {
				return {
					fontFamily: 'mono',
					weight: 'medium',
				};
			}
		},
	},
}
</script>
<script setup lang="ts">
import type { DisplayBlockProps } from '~/types/display-block';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil } from '#layers/base';
import { defineBlock } from '#layers/block-engine';
import { useFormattedDisplayValue } from '#layers/blocks/composables/use-formatted-display-value';
import { useTranslationUtils } from '#layers/i18n';
import { useDisplayTemplateNode } from './use-display-template-node';

type AcceptableValue = string | number;
type DisplayFormattedValueWeight = 'thin' | 'extralight' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black';

const props = defineProps<DisplayFormattedValueBlockProps>();

interface DisplayFormattedValueBlockProps extends DisplayBlockProps<AcceptableValue | AcceptableValue[]> {
	fontFamily?: 'sans' | 'serif' | 'mono' | 'display';
	weight?: DisplayFormattedValueWeight;

	icon?: string;
	iconColor?: string;
	/**
	 * Format the value to title case.
	 */
	convertToTitleCase?: string;
}

;

const { translateString } = useTranslationUtils();
const { locale } = useI18n();
const content = useFormattedDisplayValue({
	value: () => props.value,
	convertToTitleCase: () => props.convertToTitleCase,
	locale,
	translateString,
});
const { isNestedDisplayTemplateNode, displayTemplateNodeElement } = useDisplayTemplateNode(() => props.root);
const rootClasses = computed(() => {
	if (isNestedDisplayTemplateNode.value) {
		// The row centers this node for us, so we only lay out and truncate our content.
		return 'flex min-w-0 items-center gap-2';
	}

	return 'flex items-center gap-2 overflow-hidden';
});
</script>

<template>
	<DDisplayValueEmpty v-if="!$slots.default && isNil(value)" />

	<component :is="displayTemplateNodeElement" v-else :class="rootClasses">
		<DIcon
			v-if="icon"
			class="size-4 shrink-0"
			:name="icon"
			:style="{ color: iconColor }"
		/>
		<span
			:class="{
				'truncate': !isNestedDisplayTemplateNode,
				'min-w-0 overflow-hidden text-ellipsis whitespace-pre': isNestedDisplayTemplateNode,
				[`font-${weight}`]: weight,
				[`font-${fontFamily}`]: fontFamily,
			}"
		>
			<slot>
				{{ content }}
			</slot>
		</span>
	</component>
</template>
